import { c as _c } from "react/compiler-runtime";
import { SanitizedHtml } from '@/shared/components/ui/common/SanitizedHtml';
import { TIPOGRAFIA_TEXTO_RICO } from '@/shared/constants/tipografiaTextoRico';
import { formatDateTimeAt } from '@/shared/formatters';
import type { AlinhamentoBlocoSentenca, AssinaturaSentenca, BlocoTextoSentenca, FormatoTituloCapituloSentenca, QualificacaoParteSentenca } from '../../../types/sentencaArbitral.types';
import { classeDoAlinhamento } from '../../../utils/formatacaoSentenca';
import { TEXTOS_SENTENCA_ARBITRAL } from './secoesSentencaArbitral';
import { TrechosSentenca } from './TrechosSentenca';
const MOLDURA_LATERAL = 'mx-[37px] border-x border-solid border-neutral-200';
function RotuloContinuacao() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mb-4 flex items-center gap-2.5 pt-5"><span className="text-[9.5px] font-semibold tracking-[1.6px] whitespace-nowrap text-neutral-400 uppercase">{TEXTOS_SENTENCA_ARBITRAL.continuacaoTextoOficial}</span><div className="h-px flex-1 bg-neutral-100" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
interface AberturaTextoOficialProps {
  titulo: string;
  requerimentoNumero: string;
  qualificacoes: readonly QualificacaoParteSentenca[];
}
export function AberturaTextoOficial(t0) {
  const $ = _c(16);
  const {
    titulo,
    requerimentoNumero,
    qualificacoes
  } = t0;
  let t1;
  if ($[0] !== titulo) {
    t1 = <div className="text-brand-600 text-[17px] leading-[22px] font-bold">{titulo}</div>;
    $[0] = titulo;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== requerimentoNumero) {
    t2 = <div className="text-[12.5px] font-medium text-neutral-500">Requerimento nº {requerimentoNumero}</div>;
    $[2] = requerimentoNumero;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="bg-brand-50 border-b border-solid border-neutral-200 px-[22px] py-4 text-center">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== qualificacoes) {
    let t5;
    if ($[9] !== qualificacoes.length) {
      t5 = (qualificacao, index) => <div key={qualificacao.rotulo}><div className="mb-1 text-[12px] font-semibold tracking-[0.5px] text-neutral-400 uppercase">{qualificacao.rotulo}</div><p className={`text-justify text-[13.5px] leading-[1.65] ${index < qualificacoes.length - 1 ? "mb-[14px]" : ""} ${qualificacao.tom === "ausente" ? "text-neutral-500" : "text-neutral-700"}`}>{qualificacao.texto}</p></div>;
      $[9] = qualificacoes.length;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = qualificacoes.map(t5);
    $[7] = qualificacoes;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <div className="px-[22px] pt-[18px] pb-[26px]">{t4}</div>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t5) {
    t6 = <div className="mx-[37px] overflow-hidden rounded-t-[8px] border border-b-0 border-solid border-neutral-200">{t3}{t5}</div>;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
interface SegmentoTextoOficialProps {
  mostrarContinuacao: boolean;
  children: React.ReactNode;
}
function SegmentoTextoOficial(t0) {
  const $ = _c(5);
  const {
    mostrarContinuacao,
    children
  } = t0;
  let t1;
  if ($[0] !== mostrarContinuacao) {
    t1 = mostrarContinuacao ? <RotuloContinuacao /> : null;
    $[0] = mostrarContinuacao;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={`${MOLDURA_LATERAL} px-[22px]`} data-sentenca-texto-oficial={true}>{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
interface TituloCapituloSentencaProps {
  titulo: string;
  formato: FormatoTituloCapituloSentenca | undefined;
  mostrarContinuacao: boolean;
}
export function TituloCapituloSentenca(t0) {
  const $ = _c(6);
  const {
    titulo,
    formato,
    mostrarContinuacao
  } = t0;
  let t1;
  if ($[0] !== formato || $[1] !== titulo) {
    t1 = formato ? <TituloDoEditor nivel={formato.nivel} alinhamento={formato.alinhamento} texto={titulo} /> : <div className="mb-3 flex items-center gap-3 pt-2"><span className="text-brand-600 text-[13px] font-bold tracking-[1px] whitespace-nowrap uppercase">{titulo}</span><div className="h-px flex-1 bg-neutral-200" /></div>;
    $[0] = formato;
    $[1] = titulo;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== mostrarContinuacao || $[4] !== t1) {
    t2 = <SegmentoTextoOficial mostrarContinuacao={mostrarContinuacao}>{t1}</SegmentoTextoOficial>;
    $[3] = mostrarContinuacao;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
interface TituloDoEditorProps {
  nivel: 1 | 2 | 3;
  alinhamento: AlinhamentoBlocoSentenca;
  texto: string;
}

// Sentença v2: o título sai com a tipografia do RichTextEditor em que o árbitro o escreveu.
function TituloDoEditor(t0) {
  const $ = _c(6);
  const {
    nivel,
    alinhamento,
    texto
  } = t0;
  const Tag = `h${nivel}` as const;
  let t1;
  if ($[0] !== alinhamento) {
    t1 = classeDoAlinhamento(alinhamento);
    $[0] = alinhamento;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== Tag || $[3] !== t1 || $[4] !== texto) {
    t2 = <div className={`${TIPOGRAFIA_TEXTO_RICO} text-[13.5px] text-neutral-800`}><Tag className={t1}>{texto}</Tag></div>;
    $[2] = Tag;
    $[3] = t1;
    $[4] = texto;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
interface BlocoTextoOficialSentencaProps {
  bloco: BlocoTextoSentenca;
  mostrarContinuacao: boolean;
}
export function BlocoTextoOficialSentenca(t0) {
  const $ = _c(5);
  const {
    bloco,
    mostrarContinuacao
  } = t0;
  let t1;
  if ($[0] !== bloco) {
    t1 = <ConteudoBlocoTexto bloco={bloco} />;
    $[0] = bloco;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== mostrarContinuacao || $[3] !== t1) {
    t2 = <SegmentoTextoOficial mostrarContinuacao={mostrarContinuacao}>{t1}</SegmentoTextoOficial>;
    $[2] = mostrarContinuacao;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function ConteudoBlocoTexto(t0) {
  const $ = _c(27);
  const {
    bloco
  } = t0;
  if (bloco.tipo === "subtitulo") {
    let t1;
    if ($[0] !== bloco.alinhamento || $[1] !== bloco.texto) {
      t1 = bloco.alinhamento ? <TituloDoEditor nivel={3} alinhamento={bloco.alinhamento} texto={bloco.texto} /> : <div className="mb-2 text-[13px] font-bold text-neutral-800">{bloco.texto}</div>;
      $[0] = bloco.alinhamento;
      $[1] = bloco.texto;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (bloco.tipo === "citacao") {
    const italicoDoEditor = bloco.alinhamento ? "italic" : "";
    let t1;
    if ($[3] !== bloco.alinhamento) {
      t1 = classeDoAlinhamento(bloco.alinhamento);
      $[3] = bloco.alinhamento;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    const t2 = `border-brand-100 mb-3 ml-6 border-l-[3px] border-solid pl-[14px] text-[13px] leading-[1.7] text-neutral-600 ${t1} ${italicoDoEditor}`;
    let t3;
    if ($[5] !== bloco.trechos) {
      t3 = <TrechosSentenca trechos={bloco.trechos} />;
      $[5] = bloco.trechos;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t2 || $[8] !== t3) {
      t4 = <p className={t2}>{t3}</p>;
      $[7] = t2;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  if (bloco.tipo === "destaque") {
    let t1;
    if ($[10] !== bloco.id || $[11] !== bloco.paragrafos) {
      let t2;
      if ($[13] !== bloco.id || $[14] !== bloco.paragrafos.length) {
        t2 = (trechos, index) => <p className={`text-brand-600 text-justify text-[13.5px] leading-[1.7] ${index < bloco.paragrafos.length - 1 ? "mb-2.5" : ""}`} key={`${bloco.id}-${index}`}><TrechosSentenca trechos={trechos} /></p>;
        $[13] = bloco.id;
        $[14] = bloco.paragrafos.length;
        $[15] = t2;
      } else {
        t2 = $[15];
      }
      t1 = bloco.paragrafos.map(t2);
      $[10] = bloco.id;
      $[11] = bloco.paragrafos;
      $[12] = t1;
    } else {
      t1 = $[12];
    }
    let t2;
    if ($[16] !== t1) {
      t2 = <div className="border-brand-100 bg-brand-50 mb-3.5 rounded-[10px] border border-solid px-[22px] py-[18px]">{t1}</div>;
      $[16] = t1;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    return t2;
  }
  if (bloco.tipo === "htmlBruto") {
    let t1;
    if ($[18] !== bloco.html) {
      t1 = <SanitizedHtml className="mb-3 text-justify text-[13.5px] leading-[1.7] text-neutral-700 [&_table]:my-2 [&_table]:w-full" html={bloco.html} perfil="restrito" />;
      $[18] = bloco.html;
      $[19] = t1;
    } else {
      t1 = $[19];
    }
    return t1;
  }
  let t1;
  if ($[20] !== bloco.alinhamento) {
    t1 = classeDoAlinhamento(bloco.alinhamento);
    $[20] = bloco.alinhamento;
    $[21] = t1;
  } else {
    t1 = $[21];
  }
  const t2 = `mb-3 text-[13.5px] leading-[1.7] text-neutral-700 ${t1}`;
  let t3;
  if ($[22] !== bloco.trechos) {
    t3 = <TrechosSentenca trechos={bloco.trechos} />;
    $[22] = bloco.trechos;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== t2 || $[25] !== t3) {
    t4 = <p className={t2}>{t3}</p>;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  return t4;
}
interface FechamentoTextoOficialProps {
  assinatura: AssinaturaSentenca;
  mostrarContinuacao: boolean;
}
export function FechamentoTextoOficial(t0) {
  const $ = _c(21);
  const {
    assinatura,
    mostrarContinuacao
  } = t0;
  let t1;
  if ($[0] !== mostrarContinuacao) {
    t1 = mostrarContinuacao ? <RotuloContinuacao /> : null;
    $[0] = mostrarContinuacao;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== assinatura.nomeManuscrito) {
    t2 = <span className="font-signature text-brand-700 text-[40px] leading-[52px] sm:text-[44px] sm:leading-[56px]">{assinatura.nomeManuscrito}</span>;
    $[2] = assinatura.nomeManuscrito;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="h-px w-[320px] max-w-full bg-neutral-300" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== assinatura.nomeFormal) {
    t4 = <span className="text-[13.5px] font-semibold text-neutral-800">{assinatura.nomeFormal}</span>;
    $[5] = assinatura.nomeFormal;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== assinatura.cargo) {
    t5 = <span className="text-[12px] text-neutral-500">{assinatura.cargo}</span>;
    $[7] = assinatura.cargo;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== assinatura.assinadoEm) {
    t6 = formatDateTimeAt(assinatura.assinadoEm);
    $[9] = assinatura.assinadoEm;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <span className="text-[12px] text-neutral-400">Documento gerado e assinado eletronicamente em {t6}</span>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t2 || $[14] !== t4 || $[15] !== t5 || $[16] !== t7) {
    t8 = <div className="flex flex-col items-center gap-1.5 pt-3.5 text-center">{t2}{t3}{t4}{t5}{t7}</div>;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t1 || $[19] !== t8) {
    t9 = <div className={`${MOLDURA_LATERAL} rounded-b-[8px] border-b px-[22px] pt-5 pb-[34px]`}>{t1}{t8}</div>;
    $[18] = t1;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}